import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Sidebar from '../Sidebar/Sidebar';
import Disclaimer from "../Disclaimer/Disclaimer";
import EnvironmentNotice from "../EnvironmentNotice/EnvironmentNotice";
import WelcomeScreen from './components/WelcomeScreen';
import MessageList from './components/MessageList';
import ChatInput, { ModelPickerConfig } from './components/ChatInput';
import BoxPanel from './components/BoxPanel';
import TableauPanel from './components/TableauPanel';
import SalesforcePanel from './components/SalesforcePanel';
import FileUploadPanel from './components/FileUploadPanel';
import Modal from './components/Modal';
import ImagePreviewModal from '../ImagePreview/components/ImagePreviewModal';
import ThinkingIndicator from './components/ThinkingIndicator';
import ClarificationPanel from './components/ClarificationPanel';
import { MessageFormatter } from './MessageFormatter';
import { useUserInfo } from './hooks/useUserInfo';
import { useWebSocket } from './hooks/useWebSocket';
import { useFocusMode, FocusSubType } from './hooks/useFocusMode';
import { useDeepSearch } from './hooks/useDeepSearch';
import { useFileUpload } from './hooks/useFileUpload';
import { useAtlasIntelligence } from './hooks/useAtlasIntelligence';
import { useModelSelector, getModelPickerTypes } from './hooks/useModelSelector';

import { useTurnFeedback } from './hooks/useTurnFeedback';
import { useChatScrollBehavior } from './hooks/useChatScrollBehavior';
import { useMessageEditing } from './hooks/useMessageEditing';
import { useChatHistoryPagination } from './hooks/useChatHistoryPagination';
import { useSourceSelection } from './hooks/useSourceSelection';
import { useChatSession } from './hooks/useChatSession';
import { ChatResponseFile, Message } from './types';
import { MAX_CHAT_INPUT_CHARS } from './constants';
import { TurnRating } from './components/TurnFeedback';
import { generateUniqueKey } from './utils/helpers';
import { saveChatSelections, loadChatSelections } from './utils/chatSelectionsStorage';
import { buildChatPayload, ChatSelections } from './utils/chatPayload';
import { clearGeneratedImageCache } from '../ImagePreview/utils/generatedImageCache';
import { useImagePreview } from '../ImagePreview/hooks/useImagePreview';
import DocPreviewDock from '../DocPreview/DocPreviewDock';
import { useDocPreview } from '../DocPreview/hooks/useDocPreview';
import { mapMimeToPreviewType } from '../DocPreview/types';
import './Chat.css';

const Chat: React.FC = () => {
  const navigate = useNavigate();

  // Read session ID from URL — only present when navigating to an EXISTING chat
  // e.g. /chat/abc-123 → urlSessionId = "abc-123"
  // For new chats the URL stays at /chat and this is undefined.
  const { sessionId: urlSessionId } = useParams<{ sessionId: string }>();

  // ── UI state ──────────────────────────────────────────────────────────────
  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // ── Session ID ────────────────────────────────────────────────────────────
  // Stored as a ref (not state) so updating it never triggers a re-render.
  // This prevents React Router from seeing a new urlSessionId and re-mounting
  // the component (which would wipe messages via the history useEffect).
  const sessionIdRef = useRef<string | null>(null);

  // Tracks whether we've already updated the browser URL for this session.
  // Ref so it doesn't cause re-renders and is safe in async callbacks.
  const hasUpdatedUrl = useRef(false);

  // ── isNewChat flag ────────────────────────────────────────────────────────
  // Tells the backend whether this is a brand new conversation.
  //
  // TRUE  → every message in a new chat (until user navigates away or starts
  //         a new chat). Stays true for the lifetime of the new conversation.
  // FALSE → only when resuming from history (urlSessionId present on mount).
  //
  // Stored as a ref so:
  //   1. It never causes re-renders.
  //   2. It never goes stale in closures (send helpers always read current value).
  //   3. It resets cleanly on chat:new without triggering useEffect chains.
  const isNewChatRef = useRef(true);

  // Tracks the session ID of the current "new chat" so we can restore
  // isNewChat = true when navigating back from a history session.
  const newChatSessionIdRef = useRef<string | null>(null);

  // ── History pagination ────────────────────────────────────────────────────
  // chatMessagesRef: ref on the scroll container for scroll-to-top detection
  const chatMessagesRef = useRef<HTMLDivElement>(null);

  // ── Edit-message state ────────────────────────────────────────────────────
  const { editingMessageId, editDraft, setEditDraft, editRef, startEdit, cancelEdit } = useMessageEditing();

  // ── Turn feedback (thumbs up/down) ────────────────────────────────────────
  const { turnRatings, handleTurnRated, resetTurnRatings } = useTurnFeedback();

  // ── Generated image preview ─────────────────────────────────────────────────
  // Single shared modal for viewing a generated image at full size (see
  // ImagePreviewModal.tsx for why this lives here instead of per-thumbnail).
  const { imagePreview, openImagePreview, closeImagePreview } = useImagePreview();

  // ── Refs ──────────────────────────────────────────────────────────────────
  const messagesEndRef = useRef<HTMLDivElement>(null);  // auto-scroll anchor

  // ── Auth / user ───────────────────────────────────────────────────────────
  const { user } = useAuth();
  const { email, displayName, resolveEmail } = useUserInfo();

  // Mirrors email/user into refs so getFocusUserId reads the CURRENT values
  // even when called from a closure that's frozen at an earlier point in
  // time. This matters because useWebSocket only rebinds its socket's
  // onmessage handler when the socket itself is (re)created (see that
  // hook) — not on every render — so the onFilesReceived callback it holds
  // can still be the very first render's closure, from before email had
  // resolved (resolveEmail() finishes asynchronously, after mount). Every
  // other call site of getFocusUserId (focus mode, file upload) is invoked
  // fresh from a live render anyway, so this only actually changes behavior
  // for that one WebSocket-routed path — but reading through refs here
  // keeps getFocusUserId correct regardless of who's calling it or when.
  const emailRef = useRef(email);
  const userIdRef = useRef(user?.userId);
  useEffect(() => { emailRef.current = email; }, [email]);
  useEffect(() => { userIdRef.current = user?.userId; }, [user?.userId]);

  // Shared user-identifier getter (prefers email over numeric userId) — used
  // by focus mode, file upload, and file preview alike wherever the backend
  // needs to know who's asking. Defined ahead of the hooks below since a
  // couple of them (useDocPreview, useWebSocket's files callback) need it.
  const getFocusUserId = () => {
    return emailRef.current ?? userIdRef.current ?? '';
  };

  // ── File preview ──────────────────────────────────────────────────────────
  // Resolves a chat-response file (pdf/docx/xlsx/csv) to a fresh SAS URL and
  // holds whichever one is currently open in the side panel. Wired into
  // useWebSocket below so a matching file auto-opens the moment it arrives.
  const { previewFile, isResolving, resolveError, openFile, refreshLink, closePreview } = useDocPreview(getFocusUserId);

  // ── WebSocket ─────────────────────────────────────────────────────────────
  // useWebSocket manages the WS connection, incoming message streaming,
  // thinking indicator state, and stop functionality.
  // The deepSearch callback keeps deepSearch in sync if the backend toggles it.
  // The files callback auto-opens the first previewable file (pdf/docx/xlsx/
  // csv) from a new response — images and any other type are left alone,
  // that's a separate, already-shipped pipeline's job.
  const {
    messages,
    setMessages,
    isLoading,
    setIsLoading,
    error,
    setError,
    isAssistantActive,      // true when the WS connection is live
    thinkingSessionId,
    thinkingText,
    setThinkingText,
    setThinkingSessionId,
    pendingClarification,   // questions from a `clarification` message awaiting answers
    setPendingClarification,
    latestUserMessageId,    // id of the most recent user message (for edit/resend logic)
    lastUserRequestAtRef,   // timestamp of last send (for de-duplication)
    agentResponseCountRef,  // live response count for the CURRENT session (title-generation trigger)
    sendPayloadSafely,      // wraps WS send with reconnect logic
    resetActiveTurn,        // abandons whatever's in flight (Stop, new chat, session switch)
  } = useWebSocket(
    email,
    user?.userId,
    isNewChatRef,
    (value) => {
      setDeepSearch(value);
      if (!value) {
        setIsModeMenuOpen(false);
      }
    },
    (files: ChatResponseFile[]) => {
      const first = files.find((f) => mapMimeToPreviewType(f.file_type) !== null);
      if (first) void openFile(first);
    },
  );

  const {
    openFocusSource,
    focusLoading,
    focusError,
    focusInput,
    focusItems,
    focusPath,
    tableauItems,
    salesforceOptions,
    boxSelection,
    tableauSelection,
    salesforceSelection,
    setFocusInput,
    openFocusSourceModal,
    closeFocusModal,
    navigateIntoBoxFolder,
    navigateToBoxBreadcrumb,
    submitBoxSelection,
    submitTableauSelection,
    submitSalesforceSelection,
    restoreFocusSelections,
    clearFocusSelection,
    clearAllFocusSelections,
  } = useFocusMode(getFocusUserId);

  // ── Deep search / mode menu ───────────────────────────────────────────────
  // deepSearch: whether the "deep research" mode is active for the next query
  // selectedSources: multi-select — any combination of atlas/box/box-jobit/
  //   salesforce/tableau/jo-website/image_generation can be active at once.
  //   Empty = "All".
  // isModeMenuOpen: controls the small mode picker popup above the input
  const {
    deepSearch,
    webSearch,
    composeMode,
    selectedSources,
    isModeMenuOpen,
    modeMenuRef,
    modeMenuTriggerRef,
    setDeepSearch,
    setWebSearch,
    setComposeMode,
    setSelectedSources,
    setIsModeMenuOpen,
  } = useDeepSearch();

  const isAtlasSelected = selectedSources.has('atlas');

  // ── Atlas Lead Intelligence ─────────────────────────────────────────────────
  // atlasEnabled: whether this user has a resolvable Atlas user_id. Gates
  // whether the "Atlas Lead Intelligence" source is clickable in the mode menu.
  // atlasDeepInsights / isAtlasModeMenuOpen: the chat-mode flyout nested
  // under Atlas Lead Intelligence in the Source list. isAtlasSelected is
  // passed in so the hook can reset atlasDeepInsights to false whenever
  // Atlas is removed from the selection.
  const {
    atlasEnabled,
    atlasAccessChecked,
    checkAtlasAccess,
    atlasDeepInsights,
    isAtlasModeMenuOpen,
    atlasModeMenuRef,
    atlasModeMenuTriggerRef,
    setAtlasDeepInsights,
    setIsAtlasModeMenuOpen,
  } = useAtlasIntelligence(isAtlasSelected);

  // A reopened chat can bring Atlas back before the access check has
  // finished (e.g. right after a refresh — see restoreSelectionsForThisChat).
  // Once it has, drop Atlas if this user turns out not to have access; they
  // couldn't have picked it themselves, since the menu disables it.
  useEffect(() => {
    if (atlasAccessChecked && !atlasEnabled && selectedSources.has('atlas')) {
      setSelectedSources((prev) => {
        const next = new Set(prev);
        next.delete('atlas');
        return next;
      });
    }
  }, [atlasAccessChecked, atlasEnabled, selectedSources, setSelectedSources]);

  // ── File upload ───────────────────────────────────────────────────────────
  const {
    fileUploadMode,
    uploadedFiles,
    validationError: fileValidationError,
    isFileUploadModalOpen,
    setIsFileUploadModalOpen,
    activateFileUpload,
    deactivateFileUpload,
    uploadFilesDirectly,
    deleteFile: deleteUploadedFile,
    resetForNewChat: resetFileUpload,
    restoreFileUpload,
  } = useFileUpload();

  // ── Source / mode selection orchestration ─────────────────────────────────
  const {
    openFocusModalFor,
    focusSelectionNames,
    handleSourceToggle,
    handleSelectAtlasMode,
    handleDeepSearchToggle,
    handleComposeToggle,
    handleWebSearchToggle,
    handleFileUploadToggle,
    resetModeSelections,
    getSelectionSnapshot,
    applySavedSelections,
  } = useSourceSelection({
    deepSearch,
    setDeepSearch,
    composeMode,
    setComposeMode,
    webSearch,
    setWebSearch,
    selectedSources,
    setSelectedSources,
    setIsModeMenuOpen,
    focusLoading,
    openFocusSourceModal,
    closeFocusModal,
    clearFocusSelection,
    clearAllFocusSelections,
    restoreFocusSelections,
    boxSelection,
    tableauSelection,
    salesforceSelection,
    fileUploadMode,
    deactivateFileUpload,
    activateFileUpload,
    restoreFileUpload,
    setIsFileUploadModalOpen,
    atlasDeepInsights,
    setAtlasDeepInsights,
    setIsAtlasModeMenuOpen,
    email,
    sessionIdRef,
  });

  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // ── Model selector ─────────────────────────────────────────────────────────
  // Lets the user pick which LLMs (across providers) handle their message —
  // a chat model, an image model (Create Image), or both when Create Image is
  // combined with other selections, each from its own picker (see
  // useModelSelector). Locks to the chat model's provider on the first send
  // of a brand-new chat; resumed history chats always stay unlocked.
  const {
    providersByType,
    selectedModelIds,
    lockedProviderId,
    modelsStatus,
    openModelMenu,
    modelMenuRef,
    modelMenuTriggerRef,
    toggleModelMenu,
    selectModel,
    lockProviderForSend,
    resetModelSelection,
    restoreModels,
    fetchModels,
  } = useModelSelector(isNewChatRef);

  // The model pickers the input bar shows, top to bottom — the chat picker,
  // the image picker, or both (image above chat). The same list decides
  // which models a message sends (utils/chatPayload's buildModelFields). The provider lock
  // only ever applies to the chat picker.
  const modelPickerTypes = getModelPickerTypes(selectedSources, { fileUploadMode, webSearch });
  const modelPickers: ModelPickerConfig[] = modelPickerTypes
    .map((type) => ({
      type,
      providers: providersByType[type],
      selectedModelId: selectedModelIds[type],
      lockedProviderId: type === 'chat' ? lockedProviderId : null,
    }));

  // ── History pagination ────────────────────────────────────────────────────
  const { isLoadingHistory, resetHistoryPagination } = useChatHistoryPagination({
    email,
    urlSessionId,
    chatMessagesRef,
    messagesEndRef,
    setMessages,
    resetTurnRatings,
    resetActiveTurn,
    clearGeneratedImageCache,
  });

  // ── Scroll behavior ───────────────────────────────────────────────────────
  const { showScrollToBottom, scrollToBottom } = useChatScrollBehavior(
    chatMessagesRef,
    messagesEndRef,
    messages,
    isLoadingHistory,
  );

  // ── On mount: resolve the user's email from Okta / session storage ────────
  useEffect(() => {
    resolveEmail();
  }, []);

  // Once the user's email is resolved, check whether they have Atlas access.
  useEffect(() => {
    if (email) {
      void checkAtlasAccess(email);
    }
  }, [email]);

  // ── Saved picks per chat ──────────────────────────────────────────────────
  // A chat's picks (sources, modes, Box/Tableau/Salesforce selections, chat
  // and image models) are saved in this browser on every send
  // (rememberSelectionsForThisChat, below) and put back whenever that chat is
  // opened again — from history, or after a refresh. See
  // chatSelectionsStorage. Runs from onSessionSwitch right after its resets,
  // so a chat with nothing saved keeps the defaults.
  const restoreSelectionsForThisChat = () => {
    const sessionId = sessionIdRef.current;
    if (!email || !sessionId) return;
    const saved = loadChatSelections(email, sessionId);
    if (!saved) return;
    // Atlas is only left out here when access is already known to be denied;
    // if the check is still running, the Atlas effect above handles it.
    applySavedSelections(saved, !(atlasAccessChecked && !atlasEnabled));
    restoreModels({ chat: saved.model, image: saved.imageModel });
  };

  // ── Session identity ──────────────────────────────────────────────────────
  // Reset file upload/model selection/preview/+ menu modes when switching to
  // an existing history session — files, model lock, the preview panel, and
  // the picked sources/modes are all session-scoped. Composed here (not inside the hook) since those resets
  // belong to useFileUpload/useModelSelector/useDocPreview, not this hook.
  // Then the picks that chat's latest message used are put back, if saved.
  const { pushSessionUrl, resetSessionForNewChat } = useChatSession({
    email,
    urlSessionId,
    sessionIdRef,
    hasUpdatedUrl,
    isNewChatRef,
    newChatSessionIdRef,
    agentResponseCountRef,
    onSessionSwitch: () => {
      resetFileUpload();
      resetModelSelection();
      resetModeSelections();
      closePreview();
      restoreSelectionsForThisChat();
    },
  });

  // ── Listen for "new chat" events dispatched by the Sidebar ───────────────
  // When the user clicks "New chat", the sidebar fires chat:new.
  // We reset all conversation state and navigate back to /chat.
  useEffect(() => {
    const handler = () => {
      // Reset session identity and fire the same abandoned-chat title
      // generation as the history-switch effect inside useChatSession.
      resetSessionForNewChat();
      setMessages([]);
      resetTurnRatings();
      setInputMessage('');
      setError('');
      // Whatever was in flight for the chat we're leaving is now orphaned —
      // the backend isn't cancelled, so without this its eventual response
      // would otherwise land in this brand-new, empty conversation.
      resetActiveTurn();
      // Reset history pagination so the next session starts clean
      resetHistoryPagination();
      // Clear stored session IDs so useWebSocket's isNewSession check
      // correctly fires again on the first message of the next conversation.
      localStorage.removeItem('chat_session_id');
      localStorage.removeItem('session_id');
      // Files are session-scoped — clear upload state for the new session
      resetFileUpload();
      // New chat starts unlocked, back on the default model
      resetModelSelection();
      // Same reasoning — don't leave a file from the old conversation open
      // over a brand-new one.
      closePreview();
      // Same reasoning as the history-switch effect above — don't carry
      // generated images from the chat we're leaving into the new one.
      clearGeneratedImageCache();
      navigate('/chat');
    };
    window.addEventListener('chat:new', handler);
    return () => window.removeEventListener('chat:new', handler);
  }, [navigate]);

  // ── Listen for "copy to chat" events from the document preview ───────────
  // Fired by DocPreviewShell when the user selects text in a PDF/DOCX
  // preview and clicks the popup. Appends onto whatever's already drafted
  // rather than replacing it, so this composes with normal typing.
  useEffect(() => {
    const handler = (e: Event) => {
      const text = (e as CustomEvent<{ text: string }>).detail?.text;
      if (!text) return;
      // A programmatic value isn't limited by the textarea's maxLength, so
      // clamp here to keep this path under the same cap as typing/pasting.
      setInputMessage((prev) => (prev.trim() ? `${prev}\n${text}` : text).slice(0, MAX_CHAT_INPUT_CHARS));
    };
    window.addEventListener('chat:insert_text', handler);
    return () => window.removeEventListener('chat:insert_text', handler);
  }, []);

  // ── What every message is sent with (see utils/chatPayload) ──────────────
  // Read at the moment of sending, so it's always the current picks.
  const currentSelections = (): ChatSelections => ({
    selectedSources,
    deepSearch,
    webSearch,
    composeMode,
    atlasDeepInsights,
    fileUploadMode,
    uploadedFiles,
    boxSelection,
    tableauSelection,
    salesforceSelection,
    salesforceOptions,
    selectedModelIds,
    modelPickerTypes,
    isNewChat: isNewChatRef.current,
  });

  // Saves this chat's current picks as the ones its latest message used (see
  // restoreSelectionsForThisChat) — called right before every send. Both
  // models are kept, whichever pickers were showing, so reopening the chat
  // brings back each picker's last pick.
  const rememberSelectionsForThisChat = () => {
    const sessionId = sessionIdRef.current;
    if (!email || !sessionId) return;
    const selections = {
      ...getSelectionSnapshot(),
      model: selectedModelIds.chat,
      imageModel: selectedModelIds.image,
    };
    saveChatSelections(email, sessionId, selections);
  };

  // ── Send helpers ──────────────────────────────────────────────────────────
  // Shared reset before any WS send: clears stale thinking state and errors.
  // No longer needs to re-arm anything against Stop — sendPayloadSafely
  // already does that by setting activeTurnIdRef to the new turn_id.
  const prepareForSend = () => {
    setThinkingText('');
    setThinkingSessionId(null);
    setIsLoading(true);
    setError('');
  };

  // Sends one turn with the current picks: builds the request (see
  // utils/chatPayload), saves the picks as this chat's latest, puts the chat's
  // id in the URL and sends. Every way of sending ends here, after its own
  // checks, all of which already require a session id.
  const sendTurn = (query: string, clarificationAnswers?: string[]) => {
    const sessionId = sessionIdRef.current;
    if (!sessionId) return;
    const selections = currentSelections();
    const turnId = generateUniqueKey();
    const payload = buildChatPayload(selections, { sessionId, turnId, query, clarificationAnswers });

    rememberSelectionsForThisChat();
    // window.history.replaceState updates the URL bar without React Router
    // re-rendering — avoids the message wipe bug from navigate().
    pushSessionUrl();
    sendPayloadSafely(JSON.stringify(payload), turnId);
  };

  // Appends a new user message and sends it WITHOUT truncating history.
  // Used when editing an older (non-latest) message — we treat it as a
  // fresh query rather than a regeneration.
  const sendAsNewQuery = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || !sessionIdRef.current) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      content: trimmed,
      sender: 'user',
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    prepareForSend();
    lockProviderForSend();
    sendTurn(trimmed);
  };

  // Saves an edited message and re-sends:
  // - If it's the LATEST user message: update it in place, truncate everything
  //   after it (removes the old assistant reply), and regenerate.
  // - If it's an OLDER message: append as a new query (preserves history).
  const saveEditAndResend = async (msgId: string) => {
    const newText = editDraft.trim();
    if (!newText || !sessionIdRef.current) return;

    const idx = messages.findIndex((m) => m.id === msgId);
    if (idx === -1) return;

    cancelEdit();

    if (msgId === latestUserMessageId) {
      // Truncate: keep everything up to and including the edited message
      const updated = [...messages];
      updated[idx] = { ...updated[idx], content: newText };
      setMessages(updated.slice(0, idx + 1));
      prepareForSend();
      lockProviderForSend();
      sendTurn(newText);
      return;
    }

    // Older message — just append as a new query
    await sendAsNewQuery(newText);
  };

  // Re-sends an existing user message without editing the text:
  // - Latest message: truncate after it and regenerate the assistant reply.
  // - Older message: append as a new query.
  const resendSmart = (msgId: string) => {
    const idx = messages.findIndex((m) => m.id === msgId);
    if (idx === -1) return;

    const msg = messages[idx];
    if (msg.sender !== 'user') return;

    const text = msg.content.trim();
    if (!text || !sessionIdRef.current) return;

    if (latestUserMessageId && msgId === latestUserMessageId) {
      setMessages(messages.slice(0, idx + 1));
      prepareForSend();
      lockProviderForSend();
      sendTurn(text);
      return;
    }

    void sendAsNewQuery(text);
  };

  // Primary send — triggered by the ChatInput form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedMessage = inputMessage.trim();
    if (!trimmedMessage || isLoading || uploadedFiles.some((f) => f.status === 'uploading') || !sessionIdRef.current) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      content: trimmedMessage,
      sender: 'user',
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    prepareForSend();
    lockProviderForSend();
    lastUserRequestAtRef.current = Date.now();
    sendTurn(trimmedMessage);
  };

  // Sends the clarification panel's answers back as a new turn — the normal
  // send payload plus clarification_answers (one string per question), with
  // the answers comma-joined as the query. The questions and answers are
  // added to the thread as an assistant/user pair so the exchange reads
  // naturally once the panel is gone.
  const submitClarificationAnswers = (answers: string[]) => {
    if (!pendingClarification || !sessionIdRef.current) return;

    const query = answers.join(',');
    const now = Date.now();
    const questionsMessage: Message = {
      id: `assistant-${now}`,
      content: MessageFormatter.formatMessage(
        pendingClarification.map((q, i) => `${i + 1}. ${q.question}`).join('\n'),
      ),
      sender: 'assistant',
      timestamp: new Date(now).toISOString(),
      isMarkdown: true,
    };
    const userMessage: Message = {
      id: `user-${now}`,
      content: query,
      sender: 'user',
      timestamp: new Date(now).toISOString(),
    };

    setMessages((prev) => [...prev, questionsMessage, userMessage]);
    setPendingClarification(null);
    prepareForSend();
    lockProviderForSend();
    lastUserRequestAtRef.current = now;
    sendTurn(query, answers);
  };

  // Wraps setInputMessage specifically for the textarea's own onChange —
  // deliberately NOT used by the chat:insert_text listener (the "copy to
  // chat" feature also calls setInputMessage directly), so pulling a
  // selection out of an open preview never closes the very panel it came
  // from. Only in a brand-new conversation: closing the panel the moment
  // someone starts a fresh message keeps a resumed-history session's panel
  // open while composing a follow-up about the same file (see loadHistoryPage's
  // page===1 auto-open, which only fires there in the first place).
  const handleInputChange = (value: string) => {
    setInputMessage(value);
    if (isNewChatRef.current && value.trim()) {
      closePreview();
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="chat-screen">
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />

      <div
        className={`chat-main${isDraggingOver ? ' drag-over' : ''}`}
        onDragEnter={(e) => { e.preventDefault(); setIsDraggingOver(true); }}
        onDragOver={(e) => { e.preventDefault(); }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDraggingOver(false);
        }}
        onDrop={async (e) => {
          e.preventDefault();
          setIsDraggingOver(false);
          const files = Array.from(e.dataTransfer.files);
          if (!files.length) return;
          let currentFiles = uploadedFiles;
          if (!fileUploadMode) {
            await activateFileUpload(sessionIdRef.current ?? '', email ?? '');
            currentFiles = [];
          }
          void uploadFilesDirectly(files, sessionIdRef.current ?? '', email ?? '', currentFiles);
        }}
      >
        <DocPreviewDock
          previewFile={previewFile}
          isResolving={isResolving}
          resolveError={resolveError}
          onClose={closePreview}
          onRefreshLink={refreshLink}
        >
        <div className="chat-container">
          <div className="chat-header">
            <button className="model-selector">
              {/* Green dot when WS is connected, grey when not */}
              <div
                className={`model-indicator ${isAssistantActive ? 'active' : 'inactive'}`}
                title={isAssistantActive ? 'Assistant is active' : 'Assistant is inactive'}
              />
              <span>JO Enterprise Chat</span>
            </button>
          </div>

          {/* ref added to this div for scroll-to-top detection (history lazy load) */}
          <div className="chat-messages" ref={chatMessagesRef}>

            {/* Shown at the top while fetching an older history page on scroll */}
            {isLoadingHistory && (
              <div className="history-loading-indicator">
                <i className="fas fa-spinner fa-spin" />
                <span>Loading history…</span>
              </div>
            )}

            {/* Show welcome screen only when there are no messages and nothing is loading */}
            {messages.length === 0 && !isLoadingHistory ? (
              <WelcomeScreen displayName={displayName} email={email} userId={user?.userId} />
            ) : (
              <MessageList
                messages={messages}
                isLoading={isLoading}
                thinkingText={thinkingText}
                editingMessageId={editingMessageId}
                editDraft={editDraft}
                userId={email ?? user?.userId ?? ''}
                sessionId={sessionIdRef.current}
                turnRatings={turnRatings}
                onTurnRated={handleTurnRated}
                onEdit={startEdit}
                onCancelEdit={cancelEdit}
                onSaveEdit={saveEditAndResend}
                onEditDraftChange={setEditDraft}
                onResend={resendSmart}
                onOpenFile={openFile}
                activePath={previewFile?.path ?? null}
                onOpenImagePreview={openImagePreview}
                ThinkingIndicatorComponent={<ThinkingIndicator thinkingText={thinkingText} />}
              />
            )}
            {/* Invisible div at the bottom; scrolled into view on new messages */}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-container">
            {showScrollToBottom && (
              <button
                type="button"
                className="scroll-to-bottom-btn"
                aria-label="Scroll to latest message"
                title="Scroll to latest"
                onClick={scrollToBottom}
              >
                <i className="fas fa-arrow-down" />
              </button>
            )}
            <div className="chat-input-wrapper">
              {error && (
                <div className="error-banner">
                  <i className="fas fa-exclamation-triangle"></i>
                  {error}
                  <button onClick={() => setError('')}>
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              )}

              {pendingClarification ? (
                <ClarificationPanel
                  questions={pendingClarification}
                  onSubmit={submitClarificationAnswers}
                />
              ) : (
                <ChatInput
                  inputMessage={inputMessage}
                  isLoading={isLoading || uploadedFiles.some((f) => f.status === 'uploading')}
                  deepSearch={deepSearch}
                  webSearch={webSearch}
                  composeMode={composeMode}
                  selectedSources={selectedSources}
                  isModeMenuOpen={isModeMenuOpen}
                  messagesLength={messages.length}
                  modeMenuRef={modeMenuRef}
                  modeMenuTriggerRef={modeMenuTriggerRef}
                  fileUploadMode={fileUploadMode}
                  stagedFileCount={uploadedFiles.filter((f) => f.status === 'uploading').length}
                  doneFileCount={uploadedFiles.filter((f) => f.status === 'done').length}
                  atlasEnabled={atlasEnabled}
                  atlasDeepInsights={atlasDeepInsights}
                  isAtlasModeMenuOpen={isAtlasModeMenuOpen}
                  atlasModeMenuRef={atlasModeMenuRef}
                  atlasModeMenuTriggerRef={atlasModeMenuTriggerRef}
                  modelPickers={modelPickers}
                  modelsStatus={modelsStatus}
                  openModelMenu={openModelMenu}
                  modelMenuRef={modelMenuRef}
                  modelMenuTriggerRef={modelMenuTriggerRef}
                  onSelectModel={selectModel}
                  onToggleModelMenu={toggleModelMenu}
                  onRetryModels={fetchModels}
                  onInputChange={handleInputChange}
                  onSubmit={handleSubmit}
                  onStop={resetActiveTurn}
                  onToggleModeMenu={() => setIsModeMenuOpen((v) => !v)}
                  onToggleDeepSearch={handleDeepSearchToggle}
                  onToggleWebSearch={handleWebSearchToggle}
                  onToggleCompose={handleComposeToggle}
                  onSourceToggle={handleSourceToggle}
                  onTextareaFocus={closeFocusModal}
                  onToggleFileUpload={handleFileUploadToggle}
                  onToggleAtlasModeMenu={() => setIsAtlasModeMenuOpen((v) => !v)}
                  onSelectAtlasMode={handleSelectAtlasMode}
                  onReopenFocus={(src) => openFocusModalFor(src as FocusSubType)}
                  onReopenFileUpload={() => { closeFocusModal(); setIsFileUploadModalOpen(true); }}
                  focusSelectionNames={focusSelectionNames}
                />
              )}

              <div className="input-help">
                <strong>Disclaimer:</strong> Information is for guidance only and should not be relied upon without source and
                human validation.
                <br />
                Refer to the{' '}
                <span className="disclaimer-link" onClick={() => setIsDisclaimerOpen(true)}>
                  disclaimers
                </span>{' '}
                when using JO Enterprise Chat.
              </div>
            </div>
          </div>
        </div>
        </DocPreviewDock>

        {/* Box/Tableau/Salesforce pickers — each its own modal (staged select
            + Submit/Cancel), opened via handleSourceToggle/openFocusModalFor
            (auto-open on pick) or the chat-bar chip (reopen). Self-hides via
            isOpen when openFocusSource isn't that source. Only one of the
            three can be open at a time, but each keeps its own selection
            independently of which one is currently showing. Rendered here,
            as a direct child of .chat-main (not nested inside
            .chat-input-container/.chat-input-wrapper, which both set their
            own position:relative and would otherwise become the nearest
            positioned ancestor — anchoring the overlay to the input box
            instead of centering it in the full chat area). */}
        <BoxPanel
          isOpen={openFocusSource === 'box'}
          onCancel={closeFocusModal}
          onSubmit={submitBoxSelection}
          loading={focusLoading}
          error={focusError}
          items={focusItems}
          breadcrumb={focusPath}
          committedSelection={boxSelection}
          searchValue={focusInput}
          onSearchChange={setFocusInput}
          onNavigateInto={navigateIntoBoxFolder}
          onNavigateToBreadcrumb={navigateToBoxBreadcrumb}
        />

        <TableauPanel
          isOpen={openFocusSource === 'tableau'}
          onCancel={closeFocusModal}
          onSubmit={submitTableauSelection}
          loading={focusLoading}
          error={focusError}
          items={tableauItems}
          committedSelection={tableauSelection}
          searchValue={focusInput}
          onSearchChange={setFocusInput}
        />

        <SalesforcePanel
          isOpen={openFocusSource === 'salesforce'}
          onCancel={closeFocusModal}
          onSubmit={submitSalesforceSelection}
          loading={focusLoading}
          error={focusError}
          options={salesforceOptions}
          committedSelection={salesforceSelection}
          searchValue={focusInput}
          onSearchChange={setFocusInput}
        />

        {/* File upload — floating modal, same open/reopen pattern and same
            positioning rationale as Focus above. Its own × (onClose) only
            closes the popup, same as Focus's — turning file upload off
            entirely is the chip's × (onToggleFileUpload -> deactivateFileUpload). */}
        <Modal
          isOpen={isFileUploadModalOpen}
          onClose={() => setIsFileUploadModalOpen(false)}
          ariaLabel="File upload"
        >
          <FileUploadPanel
            uploadedFiles={uploadedFiles}
            validationError={fileValidationError}
            onUploadFiles={(files) => uploadFilesDirectly(files, sessionIdRef.current ?? '', email ?? '', uploadedFiles)}
            onDeleteFile={(fileId) => deleteUploadedFile(fileId, sessionIdRef.current ?? '', email ?? '')}
            onClose={() => setIsFileUploadModalOpen(false)}
          />
        </Modal>

        {/* Generated image preview — same top-level-of-.chat-main placement
            as Focus/File upload above. Must stay here rather than nested
            inside a message bubble; see ImagePreviewModal.tsx. */}
        <ImagePreviewModal
          isOpen={imagePreview !== null}
          onClose={closeImagePreview}
          file={imagePreview?.file ?? null}
          imageUrl={imagePreview?.url ?? null}
          userId={email ?? user?.userId ?? ''}
        />

        <Disclaimer
          open={isDisclaimerOpen}
          onClose={() => setIsDisclaimerOpen(false)}
        />

        <EnvironmentNotice />
      </div>
    </div>
  );
};

export default Chat;
